import { useMemo } from 'react';
import {
  useHardwareRentalsRelayQuery,
  RentalGraphQL,
} from '../../hardware/query/V2/useHardwareRentalsRelay';
import { MECHANIC_MEMBERSHIP_KEYWORDS } from '../../../pages/maintenance/constants';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

const isMechanicMembership = (name: string | null | undefined): boolean => {
  if (!name) return false;
  const normalized = name.toLowerCase();
  return MECHANIC_MEMBERSHIP_KEYWORDS.some((keyword) => normalized.includes(keyword));
};

const userHasActiveMechanicMembership = (rental: RentalGraphQL): boolean => {
  const memberships = rental.user?.userMemberships ?? [];
  return memberships.some(
    (um) =>
      Boolean(um.approvedOn) &&
      !um.canceledOn &&
      isMechanicMembership(um.membership?.name)
  );
};

const isWithin24hWindow = (
  rental: RentalGraphQL,
  windowEnd: Date,
  windowStart: Date
): boolean => {
  if (!rental.startTime) return false;
  const start = new Date(rental.startTime);
  if (Number.isNaN(start.getTime())) return false;
  return start >= windowStart && start <= windowEnd;
};

// Sort defaults already to START_TIME DESC in useHardwareRentalsRelayQuery, so
// don't pass a fresh array every render — that would invalidate the inner useMemo
// and trigger an infinite refetch loop.
export const useMechanicRentalsLast24h = (
  hardwareId: string | undefined,
  referenceTime: Date,
  options?: { enabled?: boolean }
) => {
  const enabled = options?.enabled !== false && Boolean(hardwareId);
  const { data, isLoading, isFetched, refetch } = useHardwareRentalsRelayQuery(
    hardwareId,
    {
      enabled,
      limit: 100,
    }
  );

  const referenceTimeMs = referenceTime.getTime();

  const rentals = useMemo(() => {
    if (!data || data.length === 0) return [];
    const windowEnd = new Date(referenceTimeMs);
    const windowStart = new Date(referenceTimeMs - TWENTY_FOUR_HOURS_MS);
    return data.filter(
      (rental) =>
        isWithin24hWindow(rental, windowEnd, windowStart) &&
        userHasActiveMechanicMembership(rental)
    );
  }, [data, referenceTimeMs]);

  return {
    rentals,
    isLoading,
    isFetched,
    refetch,
  };
};
